import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useMetrics } from './MetricsContext';
import { useProgress } from './ProgressContext';
import { hydrationRatio, hydrationTargetMl } from '../utils/hydration';

/**
 * How today's Hydration habit was completed.
 * - 'intake'  → recorded intake reached the personalized target (automatic)
 * - 'manual'  → the user explicitly chose "Marcar como concluído manualmente"
 * - null      → not completed
 */
export type HydrationCompletion = 'intake' | 'manual' | null;
interface HydrationValue {
  /** Today's personalized target, or null when no weight is recorded. */
  targetMl: number | null;
  intakeMl: number;
  ratio: number;
  weightKg: number | null;
  weightMissing: boolean;
  completion: HydrationCompletion;
  /** Logs water. Completes Hydration (15 Bee) when intake reaches the target. */
  addWater: (ml: number) => void;
  /** Explicit manual confirmation. Never changes the recorded intake. */
  markManual: () => void;
  undoManual: () => void;
}
const HydrationContext = createContext<HydrationValue | null>(null);

/** Prototype intake shown on a partially-hydrated day. */
const PARTIAL_INTAKE_ML = 1400;
export function HydrationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const metrics = useMetrics();
  const progress = useProgress();
  const weightKg = metrics.currentWeightKg;
  // Single target for every day state (including Low Energy). Recalculates
  // when the current weight changes; past days keep their stored target
  // (see HydrationDayRecord) — REQUIRES BACKEND.
  const targetMl = hydrationTargetMl(weightKg, 'kg');
  const [intakeMl, setIntakeMl] = useState<number>(() => {
    if (progress.completed.hydration) return targetMl ?? 0;
    return progress.isFirstUse ? 0 : PARTIAL_INTAKE_ML;
  });
  const [manual, setManual] = useState<boolean>(() => progress.completed.hydration && targetMl === null);
  const reachedByIntake = targetMl !== null && intakeMl >= targetMl;
  const completion: HydrationCompletion = progress.completed.hydration ? manual && !reachedByIntake ? 'manual' : 'intake' : null;
  const addWater = useCallback((ml: number) => {
    const next = intakeMl + ml;
    setIntakeMl(next);
    if (targetMl && next >= targetMl && !progress.completed.hydration) {
      progress.toggleHabit('hydration');
    }
  }, [intakeMl, targetMl, progress]);
  const markManual = useCallback(() => {
    setManual(true);
    if (!progress.completed.hydration) progress.toggleHabit('hydration');
  }, [progress]);
  const undoManual = useCallback(() => {
    setManual(false);
    if (progress.completed.hydration && !reachedByIntake) {
      progress.toggleHabit('hydration');
    }
  }, [progress, reachedByIntake]);
  const value = useMemo<HydrationValue>(() => ({
    targetMl,
    intakeMl,
    ratio: hydrationRatio(intakeMl, targetMl),
    weightKg,
    weightMissing: weightKg == null,
    completion,
    addWater,
    markManual,
    undoManual
  }), [targetMl, intakeMl, weightKg, completion, addWater, markManual, undoManual]);
  return <HydrationContext.Provider value={value} data-id="element-1414" data-mp="158ok880tr3ae">{children}</HydrationContext.Provider>;
}
export function useHydration(): HydrationValue {
  const ctx = useContext(HydrationContext);
  if (!ctx) throw new Error('useHydration must be used inside HydrationProvider');
  return ctx;
}